import { Injectable } from '@angular/core';
import { AnimationController, ModalController, ToastController } from '@ionic/angular/lazy';
import { Geolocation } from '@capacitor/geolocation';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, defer, from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Coordinates } from '../entities/coordinates.model';
import { GeolocatingModal } from '../home/geolocating-modal/geolocating.modal';
import { LoggingService } from '../shared/logging.service';

const CLE_PAR_CODE_GEOLOC: Record<string, string> = {
  'OS-PLUG-GLOC-0002': 'geolocation.permission_unavailable',
  'OS-PLUG-GLOC-0003': 'geolocation.permission_denied',
  'OS-PLUG-GLOC-0007': 'geolocation.service_desactive',
  'OS-PLUG-GLOC-0009': 'geolocation.service_desactive',
  'OS-PLUG-GLOC-0010': 'geolocation.timeout',
  'OS-PLUG-GLOC-0017': 'geolocation.service_desactive',
  'OS-PLUG-GLOC-0018': 'geolocation.permission_denied',
};

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private geolocating = false;
  private readonly geolocating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.geolocating);

  private geoLoaderModal?: HTMLIonModalElement;

  constructor(
    private readonly animationCtrl: AnimationController,
    private readonly modalController: ModalController,
    private readonly toastController: ToastController,
    private readonly translateService: TranslateService,
    private readonly log: LoggingService
  ) {}

  public getCoordinates(hideLoader = false): Observable<Coordinates | undefined> {
    const observablePosition = defer(async () => {
      const geo = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 6000, maximumAge: 20000 });
      return new Coordinates(geo.coords.latitude, geo.coords.longitude);
    });
    return this.startGeolocalizing(hideLoader).pipe(
      switchMap(() => observablePosition),
      switchMap(coords => this.stopGeolocalizing(hideLoader, coords)),
      catchError((error: unknown) => {
        this.stopGeolocalizing(hideLoader);
        this.log.warn(
          `Géolocalisation impossible (${this.codeErreur(error) ?? 'code absent'}) : l'évènement partira sans coordonnées ` +
            `et l'agent en a été averti. Message natif : ${this.messageNatif(error)}`
        );
        this.toastController
          .create({
            message: this.messageAgent(error),
            duration: 6000,
          })
          .then(toast => toast.present());
        return of(undefined);
      })
    );
  }

  private codeErreur(error: unknown): string | undefined {
    const code = (error as { code?: unknown })?.code;
    return typeof code === 'string' ? code : undefined;
  }

  private messageNatif(error: unknown): string {
    return (error as { message?: string })?.message ?? `${error}`;
  }

  private messageAgent(error: unknown): string {
    const cle = CLE_PAR_CODE_GEOLOC[this.codeErreur(error) ?? ''];
    return cle
      ? this.translateService.instant(cle)
      : this.translateService.instant('geolocation.indisponible', { code: this.codeErreur(error) ?? this.messageNatif(error) });
  }

  public isGeolocating$(): Observable<boolean> {
    return this.geolocating$.asObservable().pipe(distinctUntilChanged());
  }

  public isGeolocating(): boolean {
    return this.geolocating;
  }

  private startGeolocalizing(hideLoader: boolean): Observable<void> {
    if (!hideLoader) {
      const enterAnimation = (baseEl: HTMLElement) => {
        const root = baseEl.shadowRoot;

        const wrapperAnimation = this.animationCtrl
          .create()
          .addElement(root?.querySelector('.modal-wrapper') as Element)
          .keyframes([
            { offset: 0, opacity: '0.01', transform: 'translateY(0)', backdropFilter: 'blur(0px)' },
            { offset: 1, opacity: '1', transform: 'translateY(0)', backdropFilter: 'blur(2px)' },
          ]);

        return this.animationCtrl.create().addElement(baseEl).easing('ease-out').duration(300).addAnimation([wrapperAnimation]);
      };

      const leaveAnimation = (baseEl: any) => {
        return enterAnimation(baseEl).direction('reverse');
      };

      this.geolocating = true;
      this.geolocating$.next(true);

      return from(
        this.modalController.create({
          id: 'geolocalisation-loader-modal',
          cssClass: 'geolocalisation-loader-modal',
          component: GeolocatingModal,
          enterAnimation,
          leaveAnimation,
        })
      ).pipe(
        switchMap(modal => {
          this.geoLoaderModal = modal;
          return modal.present();
        })
      );
    } else {
      return of(void 0);
    }
  }

  private stopGeolocalizing(hideLoader: boolean, coordsToPass?: Coordinates): Observable<Coordinates | undefined> {
    if (!hideLoader) {
      this.geolocating = false;
      this.geolocating$.next(false);
      return from(this.geoLoaderModal?.dismiss() ?? Promise.resolve(true)).pipe(switchMap(() => of(coordsToPass)));
    } else {
      return of(coordsToPass);
    }
  }
}
